/**
 * ジャンル別カラー管理
 *
 * 活動年表でジャンルごとに視覚的に識別するためのカラーマッピング。
 * tailwind.config.js の genre カラーと対応。
 *
 * 注意: Tailwind CSSは動的クラス名を認識できないため、
 * 完全なクラス名を静的に定義する必要がある。
 */

/**
 * ジャンル別のスタイル定義
 *
 * 各ジャンルに対して以下のスタイルを定義:
 * - selected: 選択時の背景色+テキスト色
 * - unselected: 非選択時の背景色+テキスト色+ホバー
 * - label: カードラベル用のスタイル
 */
import type { LanguageMap } from '@/types/domain';

interface GenreStyle {
  selected: string;
  unselected: string;
  label: string;
}

const GENRE_STYLES: Record<string, GenreStyle> = {
  // 作品ジャンル
  アニメーション: {
    selected: 'bg-genre-animation text-white border border-genre-animation',
    unselected:
      'bg-genre-animation/20 text-genre-animation hover:bg-genre-animation/30 border border-genre-animation',
    label: 'bg-genre-animation text-white border border-genre-animation',
  },
  イラストレーション: {
    selected:
      'bg-genre-illustration text-white border border-genre-illustration',
    unselected:
      'bg-genre-illustration/20 text-genre-illustration hover:bg-genre-illustration/30 border border-genre-illustration',
    label: 'bg-genre-illustration text-white border border-genre-illustration',
  },
  マンガ: {
    selected: 'bg-genre-manga text-white border border-genre-manga',
    unselected:
      'bg-genre-manga/20 text-genre-manga hover:bg-genre-manga/30 border border-genre-manga',
    label: 'bg-genre-manga text-white border border-genre-manga',
  },
  文章: {
    selected: 'bg-genre-text text-white border border-genre-text',
    unselected:
      'bg-genre-text/20 text-genre-text hover:bg-genre-text/30 border border-genre-text',
    label: 'bg-genre-text text-white border border-genre-text',
  },
  本人: {
    selected: 'bg-genre-person text-white border border-genre-person',
    unselected:
      'bg-genre-person/20 text-genre-person hover:bg-genre-person/30 border border-genre-person',
    label: 'bg-genre-person text-white border border-genre-person',
  },
  立体: {
    selected: 'bg-genre-sculpture text-white border border-genre-sculpture',
    unselected:
      'bg-genre-sculpture/20 text-genre-sculpture hover:bg-genre-sculpture/30 border border-genre-sculpture',
    label: 'bg-genre-sculpture text-white border border-genre-sculpture',
  },
  視覚装置: {
    selected:
      'bg-genre-visual-device text-white border border-genre-visual-device',
    unselected:
      'bg-genre-visual-device/20 text-genre-visual-device hover:bg-genre-visual-device/30 border border-genre-visual-device',
    label:
      'bg-genre-visual-device text-white border border-genre-visual-device',
  },
  // 後方互換性のため残す（取り込み処理で分離済み）
  '視覚装置・立体': {
    selected: 'bg-genre-sculpture text-white border border-genre-sculpture',
    unselected:
      'bg-genre-sculpture/20 text-genre-sculpture hover:bg-genre-sculpture/30 border border-genre-sculpture',
    label: 'bg-genre-sculpture text-white border border-genre-sculpture',
  },
  驚き盤: {
    selected: 'bg-genre-thaumatrope text-white border border-genre-thaumatrope',
    unselected:
      'bg-genre-thaumatrope/20 text-genre-thaumatrope hover:bg-genre-thaumatrope/30 border border-genre-thaumatrope',
    label: 'bg-genre-thaumatrope text-white border border-genre-thaumatrope',
  },
  絵本: {
    selected: 'bg-genre-picturebook text-white border border-genre-picturebook',
    unselected:
      'bg-genre-picturebook/20 text-genre-picturebook hover:bg-genre-picturebook/30 border border-genre-picturebook',
    label: 'bg-genre-picturebook text-white border border-genre-picturebook',
  },
  紙芝居: {
    selected: 'bg-genre-kamishibai text-white border border-genre-kamishibai',
    unselected:
      'bg-genre-kamishibai/20 text-genre-kamishibai hover:bg-genre-kamishibai/30 border border-genre-kamishibai',
    label: 'bg-genre-kamishibai text-white border border-genre-kamishibai',
  },

  // イベントジャンル
  上映会: {
    selected: 'bg-genre-screening text-white border border-genre-screening',
    unselected:
      'bg-genre-screening/20 text-genre-screening hover:bg-genre-screening/30 border border-genre-screening',
    label: 'bg-genre-screening text-white border border-genre-screening',
  },
  展覧会: {
    selected: 'bg-genre-exhibition text-white border border-genre-exhibition',
    unselected:
      'bg-genre-exhibition/20 text-genre-exhibition hover:bg-genre-exhibition/30 border border-genre-exhibition',
    label: 'bg-genre-exhibition text-white border border-genre-exhibition',
  },
  // 後方互換性のため展示会も残す
  展示会: {
    selected: 'bg-genre-exhibition text-white border border-genre-exhibition',
    unselected:
      'bg-genre-exhibition/20 text-genre-exhibition hover:bg-genre-exhibition/30 border border-genre-exhibition',
    label: 'bg-genre-exhibition text-white border border-genre-exhibition',
  },
  イベント: {
    selected: 'bg-genre-event text-white border border-genre-event',
    unselected:
      'bg-genre-event/20 text-genre-event hover:bg-genre-event/30 border border-genre-event',
    label: 'bg-genre-event text-white border border-genre-event',
  },
};

/** デフォルトスタイル（マッピングにないジャンル用） */
const DEFAULT_STYLE: GenreStyle = {
  selected: 'bg-primary text-primary-foreground',
  unselected: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  label: 'bg-secondary text-secondary-foreground',
};

const ENGLISH_GENRE_ALIASES: Record<string, string> = {
  animation: 'アニメーション',
  illustration: 'イラストレーション',
  manga: 'マンガ',
  text: '文章',
  writing: '文章',
  person: '本人',
  self: '本人',
  sculpture: '立体',
  'visual device': '視覚装置',
  'visual device sculpture': '視覚装置・立体',
  thaumatrope: '驚き盤',
  picturebook: '絵本',
  'picture book': '絵本',
  kamishibai: '紙芝居',
  'paper theater': '紙芝居',
  screening: '上映会',
  screenings: '上映会',
  exhibition: '展覧会',
  exhibitions: '展覧会',
  event: 'イベント',
  events: 'イベント',
};

function isLanguageMap(value: unknown): value is LanguageMap {
  return (
    typeof value === 'object' &&
    value !== null &&
    !('@type' in value) &&
    ('ja' in value || 'en' in value)
  );
}

function normalizeEnglishGenre(genre: string): string | undefined {
  const normalized = genre
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
  return ENGLISH_GENRE_ALIASES[normalized];
}

export function normalizeGenre(
  genre: string | LanguageMap | undefined
): string {
  if (!genre) {
    return '';
  }

  if (typeof genre === 'string') {
    return normalizeEnglishGenre(genre) ?? genre;
  }

  if (isLanguageMap(genre)) {
    if (genre.ja) {
      return genre.ja;
    }
    if (genre.en) {
      return normalizeEnglishGenre(genre.en) ?? genre.en;
    }
    const fallback = Object.values(genre).find(
      (value): value is string =>
        typeof value === 'string' && value.trim().length > 0
    );
    if (fallback) {
      return normalizeEnglishGenre(fallback) ?? fallback;
    }
  }

  return '';
}

/**
 * ジャンル名から背景色のTailwindクラスを取得
 *
 * @param genre - ジャンル名（日本語）
 * @param isSelected - 選択状態かどうか
 * @returns Tailwindクラス文字列
 */
export function getGenreBackgroundClass(
  genre: string | LanguageMap | undefined,
  isSelected: boolean
): string {
  const normalizedGenre = normalizeGenre(genre);
  const style = GENRE_STYLES[normalizedGenre] ?? DEFAULT_STYLE;
  return isSelected ? style.selected : style.unselected;
}

/**
 * ジャンル名からラベル用のTailwindクラスを取得
 *
 * タイムラインカードのジャンルラベル用。
 * 濃い背景色+白テキストのアクティブスタイル（selectedと同じ）。
 *
 * @param genre - ジャンル名（日本語）
 * @returns Tailwindクラス文字列
 */
export function getGenreLabelClass(
  genre: string | LanguageMap | undefined
): string {
  const normalizedGenre = normalizeGenre(genre);
  const style = GENRE_STYLES[normalizedGenre] ?? DEFAULT_STYLE;
  return style.label;
}
